import React from 'react';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

type ContextMenuProps = {
  contextMenu: {
    mouseX: number | null;
    mouseY: number | null;
    childIds: string[];
    selectedId: string | null;
    isFile?: boolean;
    file_type: string;
    folder_type: string;
    section: string;
  };
  handleCloseMenu: () => void;
  handleOpenDeleteModal: () => void;
  handleOpenEditModal: () => void;
  /** Starts inline rename for a folder; omit to hide the Rename item. */
  handleRenameFolder?: () => void;
};

/**
 * Desktop-style row menu: a rounded raised panel inset by 4px, a single icon
 * column, and the destructive action set apart below a separator.
 *
 * Every colour is read from the Material theme, which the file explorer builds
 * from the active Joy colour scheme — so the menu follows light and dark
 * without a second palette to keep in step. The previous literals (a #555E68
 * label on a #F0F4F8 hover) turned near-invisible once the surface went dark.
 */
const ContextMenu: React.FC<ContextMenuProps> = ({
  contextMenu,
  handleCloseMenu,
  handleOpenDeleteModal,
  handleOpenEditModal,
  handleRenameFolder,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Only show context menu for original files
  if (
    (contextMenu.isFile && contextMenu.file_type !== 'original') ||
    contextMenu.folder_type === 'consolidated' ||
    contextMenu.folder_type === 'summary'
  ) {
    return null;
  }

  const showEdit = Boolean(contextMenu.isFile);
  const showRename = !contextMenu.isFile && Boolean(handleRenameFolder);

  const itemSx: SxProps<Theme> = {
    minHeight: 34,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    color: 'text.primary',
    // One narrow icon column, the icon kept subordinate to its label.
    '& .MuiListItemIcon-root': {
      minWidth: 0,
      mr: 1.25,
      color: 'inherit',
      opacity: 0.75,
    },
    '& .MuiSvgIcon-root': { fontSize: 18 },
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'action.hover',
    },
  };

  const destructiveItemSx: SxProps<Theme> = {
    ...itemSx,
    color: 'error.main',
    '&:hover, &.Mui-focusVisible': {
      // A red wash rather than a solid fill: legible on either surface, and it
      // keeps the item readable while it is the one under the cursor.
      backgroundColor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.09),
      color: 'error.main',
    },
  };

  return (
    <Menu
      open={contextMenu.mouseY !== null}
      onClose={handleCloseMenu}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu.mouseY !== null && contextMenu.mouseX !== null
          ? {
              top: contextMenu.mouseY + 8,
              left: contextMenu.mouseX,
            }
          : undefined
      }
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      transitionDuration={120}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            minWidth: 200,
            p: 0.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            // In dark mode the theme's paper is nearly black, which reads as a
            // hole rather than a panel — lift the surface off the background.
            backgroundColor: isDark
              ? lighten(theme.palette.background.paper, 0.08)
              : theme.palette.background.paper,
            backgroundImage: 'none',
            color: 'text.primary',
            boxShadow: isDark
              ? '0 8px 24px rgba(0, 0, 0, 0.55)'
              : '0 8px 24px rgba(16, 24, 40, 0.14)',
          },
        },
        list: { dense: true, sx: { p: 0 } },
      }}
    >
      {showEdit && (
        <MenuItem disableRipple onClick={handleOpenEditModal} sx={itemSx}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          {t('Edit')}
        </MenuItem>
      )}

      {showRename && (
        <MenuItem disableRipple onClick={handleRenameFolder} sx={itemSx}>
          <ListItemIcon>
            <DriveFileRenameOutlineIcon fontSize="small" />
          </ListItemIcon>
          {t('Rename')}
        </MenuItem>
      )}

      {(showEdit || showRename) && <Divider sx={{ my: 0.5 }} />}

      <MenuItem
        disableRipple
        onClick={handleOpenDeleteModal}
        sx={destructiveItemSx}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        {t('Delete')}
      </MenuItem>
    </Menu>
  );
};

export { ContextMenu };
